// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  CollapsibleWizard,
  Step,
} from "@components";
import type { HierarchyAssignment, ApprovableContainer } from "@modules/service/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";

import StepSettings from "./step-settings";
import StepCompliance from "./step-compliance";
import StepLevels from "./step-levels";
import AbsenceMonitoringActions from "./absence-monitoring-actions";

type OwnProps = $Exact<{
  defaultStep: 1 | 2 | 3 | 4,
  clientId: number,
  onCancel: () => void,
}>;

type PropsFromState = $Exact<{
  schema: Object,
  container: ?ApprovableContainer<AbsenceMonitoring>,
  draft: ?AbsenceMonitoring,
  assignments: Array<HierarchyAssignment>,
}>;

type PropsFromDispatch = $Exact<{
  update: typeof actionCreators.UpdateAbsenceMonitoring,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class AbsenceMonitoringEditModal extends React.Component<Props> {
  validator: *;

  get schema() {
    return this.props.schema;
  }

  get isValid() {
    this.validator = new Ajv().compile(this.schema);
    if (!this.validator || !this.props.draft) {
      return false;
    }

    const draft = removeNullAndUndefinedProps(this.props.draft?.content || {});
    let isValid = this.validator(draft);
    if (!isValid) {
      console.warn("AbsenceMonitoringEditModal validation error: ", this.validator.errors);
    }

    if (draft && !draft.noActionReasons.find(el => el.isActive === true)) {
      isValid = false;
    }

    const withKpiTarget = isValid && draft && draft.tasks.every(item => item.kpiTarget);
    if (!withKpiTarget) {
      return false;
    }

    const validSupportAction = (draft.additionalSupportActions || []).find(el => el.isActive);
    if (!validSupportAction) {
      return false;
    }

    return isValid;
  }

  handleSubmit = () => {
    if (this.props.container && this.props.draft) {
      this.props.update({
        clientId: this.props.clientId,
        processId: (this.props.container || {}).id,
        draft: this.props.draft.content,
      });
      this.props.onCancel();
    }
  };

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.props.onCancel}>
          {this.props.t("ABSENCE_MONITORING_EDIT_TITLE", {
            processId: (this.props.draft || {}).id,
          })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <CollapsibleWizard
            defaultStep={this.props.defaultStep}
            data={[
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
            ]}
          >
            <Step
              component={StepSettings}
              title={this.props.t("ABSENCE_MONITORING_STEP1")}
              editing
            />
            <Step
              component={StepCompliance}
              title={this.props.t("ABSENCE_MONITORING_STEP2_COMPLIANCE")}
              editing
            />
            <Step component={StepLevels} title={this.props.t("ABSENCE_MONITORING_STEP3")} editing />
            <Step
              component={AbsenceMonitoringActions}
              title={this.props.t("ACTION_LISTS")}
              editing
            />
          </CollapsibleWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="save" disabled={!this.isValid} onClick={this.handleSubmit}>
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    container: serviceSelectors.getCurrentAbsenceMonitoring(state),
    schema: serviceSelectors.getAbsenceMonitoringSchema(state),
    draft: serviceSelectors.getAbsenceMonitoringDraft(state),
    assignments: serviceSelectors.getAbsenceMonitoringAssignments(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  update: actionCreators.UpdateAbsenceMonitoring,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceMonitoringEditModal);
